type BuildInfo = { 
  version?: string; 
  sha?: string; 
  builtAt?: string; 
  environment?: string; 
};

import Utils from '@/modules/Utils';

export class VersionService {
  private static readonly CHECK_INTERVAL_PROD = 5 * 60 * 1000; // 5 minutes for production
  private static readonly CHECK_INTERVAL_DEV = 2 * 60 * 1000; // 2 minutes for dev/staging
  private static intervalId: number | null = null;
  private static onVersionChangeCallback: ((newVersion: string) => void) | null = null;
  private static abortController: AbortController | null = null;
  private static enabled = true;
  private static latestBuildInfo: BuildInfo | null = null;
  private static isChecking = false;

  /**
   * Get current version from environment variables
   */
  static getCurrentVersion(): string {
    const envVersion = process.env.VUE_APP_VERSION;
    
    // Skip version checking on localhost
    if (this.isLocalhost()) {
      this.enabled = false;
      return envVersion || 'localhost-dev';
    }
    
    return envVersion || '0.0.0';
  }

  /**
   * Check if running on localhost
   */
  static isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.startsWith('192.168.') ||
           window.location.hostname === 'dev.tondo-iot.com' ||
           window.location.hostname.endsWith('.local');
  }

  /**
   * Check if we're in production environment
   */
  static isProduction(): boolean {
    return process.env.NODE_ENV === 'production';
  }

  /**
   * Get latest build info from server
   */
  static getLatestBuildInfo(): BuildInfo | null {
    return this.latestBuildInfo;
  }

  /**
   * Check if version has changed by comparing environment version with server build info
   */
  static hasVersionChanged(): boolean {
    const currentVersion = this.getCurrentVersion();
    const serverBuildInfo = this.getLatestBuildInfo();
    
    return serverBuildInfo?.version !== undefined && serverBuildInfo.version !== currentVersion;
  }

  /**
   * Fetch build info from deployed build-info.json
   */
  static async fetchBuildInfo(): Promise<BuildInfo | null> {
    if (!this.enabled) {
      return null;
    }

    try {
      // Abort previous request if still pending
      this.abortController?.abort();
      this.abortController = new AbortController();

      const response = await fetch('/build-info.json?ts=' + Date.now(), {
        cache: 'no-store',
        signal: this.abortController.signal
      });
      
      if (!response.ok) {
        return null;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return null;
      }
      
      const buildInfo = await response.json() as BuildInfo;
      this.latestBuildInfo = buildInfo;
      return buildInfo;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('VersionService: Request aborted');
      } else if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error('Unknown error', error);
      }
      return null;
    }
  }

  /**
   * Initialize version checking
   */
  static initialize(onVersionChange?: (newVersion: string) => void): void {
    // Skip if disabled (localhost)
    if (!this.enabled) {
      return;
    }

    // Store callback only if enabled
    if (onVersionChange) {
      this.onVersionChangeCallback = onVersionChange;
    }

    // Start periodic checking for ALL environments to detect server updates
    // Production: checks every 5 minutes for new releases
    // Dev/Staging: checks every 30 seconds for new deployments
    this.startPeriodicCheck();
  }

  /**
   * Start periodic version checking (for all environments)
   */
  static startPeriodicCheck(): void {
    if (!this.enabled || this.intervalId) {
      return; // Disabled or already running
    }

    const checkInterval = this.isProduction() ? this.CHECK_INTERVAL_PROD : this.CHECK_INTERVAL_DEV;
    // Add visibility change listener for tab focus checks
    this.addVisibilityListener();

    this.intervalId = window.setInterval(async () => {
      await this.checkForUpdates();
    }, checkInterval);
  }

  /**
   * Check for version updates
   */
  static async checkForUpdates(): Promise<void> {
    if (!this.enabled || this.isChecking) return;

    this.isChecking = true;
    try {
      const buildInfo = await this.fetchBuildInfo();
      const currentVersion = this.getCurrentVersion();
      
      if (buildInfo?.version && buildInfo.version !== currentVersion && this.onVersionChangeCallback) {
        this.onVersionChangeCallback(buildInfo.version);
      }
    } catch (error) {
      console.error('VersionService: Error checking for updates:', error);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Add visibility change listener for tab focus checks
   */
  static addVisibilityListener(): void {
    // Remove any existing listeners to prevent duplicates
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Visibility change handler
   */
  static onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      VersionService.checkForUpdates();
    }
  };

  /**
   * Stop periodic version checking
   */
  static stopPeriodicCheck(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    // Abort any pending requests
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * Get latest build info for display
   */
  static getLatestVersionInfo(): { version: string; buildInfo: BuildInfo | null } {
    const currentVersion = this.getCurrentVersion();
    return {
      version: currentVersion,
      buildInfo: this.latestBuildInfo
    };
  }

  /**
   * Refresh the application
   */
  static async refreshApplication(): Promise<void> {
    try {
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
      
      // Clear caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Force reload
      window.location.reload();
    } catch (error) {
      console.error('Failed to refresh application:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }

  /**
   * Get version display format based on environment
   */
  static getVersionDisplay(version: string, timezone: string): string {
    if (!version) {
      return 'Unknown Version';
    }
    
    if (this.isProduction()) {
      return `${version}`;
    }
    
    // For development/staging, try to convert timestamp
    try {
      return Utils.convertTimestamp(version, 'DD-MM-YYYY HH:mm:ss', timezone);
    } catch (error) {
      console.warn('VersionService: Failed to convert timestamp:', error);
      return version; // Fallback to original version string
    }
  }
}
